import React from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw, LogOut } from "lucide-react";
import { Button } from "./ui/button";
import { HeroGlow } from "./ui/hero";
import { WavesLogo } from "./WavesLogo";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Application error:", error, errorInfo);
    // An error boundary swallows the error, so Sentry's global handlers never
    // see it in production builds — report it explicitly.
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleLogout = async () => {
    try {
      await fetch("/api/auth/signout", {
        method: "POST",
        credentials: "same-origin",
      });
    } catch {
      // ignore
    }
    window.location.href = "/login";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-b from-white to-waves-subtle px-4 text-waves-ink">
          <HeroGlow />
          <div className="relative z-[1] flex w-full max-w-md flex-col items-center gap-5 rounded-card-lg border border-waves-line bg-white p-8 text-center shadow-modal">
            <WavesLogo className="h-7" />
            <div className="flex h-14 w-14 items-center justify-center rounded-pill bg-status-alert-bg text-status-alert-fg">
              <AlertTriangle size={26} />
            </div>
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-black tracking-[-0.02em]">Er is iets <span className="text-waves-accent">misgegaan</span>.</h1>
              <p className="text-[15px] leading-relaxed text-waves-body">
                We konden de pagina niet laden. Dit kan komen door een verlopen sessie of een tijdelijke storing.
                Probeer de pagina te vernieuwen of opnieuw in te loggen.
              </p>
            </div>
            <div className="flex w-full flex-col gap-3 sm:flex-row">
              <Button className="flex-1" onClick={this.handleReload}>
                <RefreshCw /> Probeer opnieuw
              </Button>
              <Button variant="outline" className="flex-1" onClick={this.handleLogout}>
                <LogOut /> Opnieuw inloggen
              </Button>
            </div>
            {this.state.error?.message && (
              <details className="w-full text-left">
                <summary className="cursor-pointer text-xs font-bold text-waves-muted hover:text-waves-ink">Technische details</summary>
                <pre className="mt-2 overflow-x-auto rounded-nav bg-waves-subtle p-3 font-mono text-xs text-waves-body">{this.state.error.message}</pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
